/* eslint-disable @next/next/no-img-element */
"use client";

import Autoplay, { AutoplayOptionsType } from "embla-carousel-autoplay";
import { EmblaCarouselType, EmblaOptionsType } from "embla-carousel";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { SpecialsCarouselEvents, trackEvent } from "@/analytics";

import { CalendarIcon } from "@heroicons/react/20/solid";
import { CarouselCTA } from "../carousel-types";
import HorizontalLoadingBar from "./HorizontalLoadingBar";
import Link from "next/link";
import Runtime from "../../common/Runtime";
import { StarsIcon } from "../../common/icons";
import { cn } from "../../common/utils";
import useEmblaCarousel from "embla-carousel-react";
import { useTimeout } from "../../common/hooks";

const getIcon = (iconName: string | undefined): React.ReactNode => {
  switch (iconName) {
    case "calendar":
      return <CalendarIcon className="h-6 w-6 text-white" aria-hidden="true" />;
    case "star":
      return <StarsIcon aria-hidden="true" />;
    default:
      return <StarsIcon aria-hidden="true" />;
  }
};

export type CarouselImage = {
  imageUrl: string;
  title?: string;
  comedianName?: string;
  cta?: CarouselCTA;
  releaseDate?: string;
};

type PropType = {
  slides: CarouselImage[];
  options?: EmblaOptionsType;
  maxHeight?: string; // used to enforce a specific aspect ratio
};

const defaultOptions: EmblaOptionsType = {
  loop: true,
};

export const SpecialsCarousel: React.FC<PropType> = ({
  options = defaultOptions,
  slides,
  maxHeight,
}) => {
  const autoplayOptions: AutoplayOptionsType = {
    delay: 8000,
    playOnInit: true,
    stopOnMouseEnter: false,
    stopOnInteraction: false,
    rootNode: (emblaRoot: HTMLElement) => emblaRoot.parentElement,
  };

  const [emblaRef, emblaApi] = useEmblaCarousel(options, [
    Autoplay(autoplayOptions),
  ]);
  const [loadingBarKey, setLoadingBarKey] = useState(0);
  const buttonsContainerRef = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);
  const [userBeganInteracting, setUserBeganInteracting] = useState(false);
  const [startTimeout, setStartTimeout] = useState(false);

  // When a user clicks on a slide, we can expect them to pause to look
  // and/or to click on other slides. As such we pause autoplay (in scrollTo())
  // and we use this timeout to resume the slide playback after 10 seconds
  // Remeber to consider as the total time the slides won't change to be
  // the timeout time + the delay defined in the autoplayOptions
  useTimeout(
    () => {
      // @ts-expect-error - emblaApi.plugins is not typed
      emblaApi?.plugins()?.autoplay?.play();
      setUserBeganInteracting(false);
    },
    startTimeout ? 6000 : null
  );

  useEffect(() => {
    if (userBeganInteracting) {
      setStartTimeout(true);
    }
  }, [startTimeout, userBeganInteracting]);

  const scrollTo = useCallback(
    (index: number) => {
      if (emblaApi) {
        // Since the user clicked buttons, pause the slides autoplay for 10 seconds
        // @ts-expect-error - emblaApi.plugins is not typed
        emblaApi.plugins()?.autoplay?.stop();
        // Resetting any existing timeout
        setStartTimeout(false);
        setUserBeganInteracting(true);

        // This is unfortunately necessary due to some surprising behavior about how
        // Embla choses the direction of scrollTo when loop is active
        // Read more if interested here: https://github.com/davidjerleke/embla-carousel/issues/109
        const currentSlideIndex = emblaApi.selectedScrollSnap();
        if (currentSlideIndex < index) {
          for (let times = 0; times < index - currentSlideIndex; times++) {
            emblaApi.scrollNext();
          }
        } else {
          for (let times = 0; times > index - currentSlideIndex; times--) {
            emblaApi.scrollPrev();
          }
        }
      }
    },
    [emblaApi]
  );

  const onInit = useCallback((emblaApi: any) => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap());
    // re-reset/re-sync loading bar
    setLoadingBarKey((prevKey) => prevKey + 1);
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on("reInit", onInit);
    emblaApi.on("reInit", onSelect);
    emblaApi.on("select", onSelect);
  }, [emblaApi, onInit, onSelect]);

  const SLIDE_STYLES =
    "flex-[0_0_100%] min-w-[0px] relative grid grid-rows-6 bg-black md:justify-center";

  return (
    <>
      <div className="relative">
        {/* viewport */}
        {/* <HorizontalLoadingBar key={loadingBarKey} count={loadingBarKey} /> */}
        <div className="overflow-hidden" ref={emblaRef}>
          {/* container */}
          <div className="flex backface-hidden touch-pan-y">
            {/* slide */}
            {slides.length > 0 &&
              slides
                .filter((slide) => slide != undefined)
                .map((s, i) => (
                  <div className={SLIDE_STYLES} key={s.cta?.SpecialId || i}>
                    <Link
                      href={`${s.cta?.redirect}`}
                      aria-label={`${s.title}${s.comedianName ? ` by ${s.comedianName}` : ""}`}
                      className="row-span-full"
                      onClick={() =>
                        trackEvent(
                          SpecialsCarouselEvents.CLICKED_ON_SPECIAL_IMAGE,
                          {
                            ComedianId: s.cta?.ComedianId,
                            SpecialId: s.cta?.SpecialId,
                          }
                        )
                      }
                    >
                      <img
                        className={cn(
                          "object-cover block w-full bg-black",
                          maxHeight || "h-full"
                        )}
                        src={s.imageUrl}
                        alt={`${s.comedianName} performing ${s.title}`}
                        aria-label={`Hero image for ${s.title}`}
                      />
                    </Link>
                    {s.releaseDate && s.releaseDate !== "Available Now!" && (
                      <div
                        className={cn(
                          "hidden text-white text-3xl row-start-4 row-end-4 z-10 absolute h-full left-[12vw] items-end mt-4",
                          i === selectedIndex && "md:flex"
                        )}
                      >
                        <span>{s.releaseDate}</span>
                      </div>
                    )}
                    {s.cta && (
                      <div className="hidden md:grid grid-rows-3 w-fit row-start-5 row-end-5 z-10 absolute left-[12vw]">
                        <Link
                          href={`${s.cta?.redirect}`}
                          className="row-start-2 row-end-3"
                        >
                          <button
                            className={
                              "btn flex items-center text-white px-4 py-3 gap-x-2"
                            }
                            onClick={() =>
                              trackEvent(
                                s.cta?.price! > 0
                                  ? SpecialsCarouselEvents.CLICKED_WATCH_NOW_PAID
                                  : SpecialsCarouselEvents.CLICKED_WATCH_NOW_FREE,
                                {
                                  ComedianId: s.cta?.ComedianId,
                                  SpecialId: s.cta?.SpecialId,
                                }
                              )
                            }
                          >
                            {getIcon(s.cta?.icon)}
                            <span> {s.cta?.message} </span>
                          </button>
                        </Link>
                        {s.cta?.lengthOfSpecial && (
                          <Runtime
                            runtime={s.cta?.lengthOfSpecial || 0}
                            className="text-black landscape:hidden md:landscape:flex md:text-white text-sm flex justify-start items-center row-start-3 row-end-3"
                          />
                        )}
                      </div>
                    )}
                  </div>
                ))}
          </div>
          <HorizontalLoadingBar key={loadingBarKey} count={loadingBarKey} />
        </div>
      </div>

      <div className="md:hidden py-4 flex justify-center w-full px-8 flex-col">
        {slides.length > 0 &&
          slides
            .filter((i) => i != undefined)
            .map((s, i) => (
              <React.Fragment key={i}>
                {s.cta && (
                  <div
                    className={cn(
                      i === selectedIndex ? "block " : "hidden",
                      "w-full"
                    )}
                  >
                    <Link href={`${s.cta?.redirect}`}>
                      <button
                        className={
                          "btn flex items-center text-white px-4 py-3 gap-x-2 w-full justify-center"
                        }
                        onClick={s.cta?.onClick}
                      >
                        {getIcon(s.cta?.icon)}
                        <span> {s.cta?.message} </span>
                      </button>
                    </Link>
                  </div>
                )}
              </React.Fragment>
            ))}
      </div>
    </>
  );
};

export default SpecialsCarousel;
